import React, { useEffect, useState, useRef } from 'react';
import { useNavigate }                        from 'react-router-dom';
import { useAuth }                            from '../contexts/AuthContext';
import {
  adminListUsers, adminLockUser, adminUnlockUser,
  adminDeactivateUser, adminGetAiUsage, adminLoadVehicleConfig,
  adminAiVehicleConfig, adminGetVersions,
} from '../api/admin';
import { refMakes, refModels, refYears, refPackages, refEngines } from '../api/vehicles';
import './AdminPage.css';

const TABS = [
  { key: 'users',    icon: '👥', label: 'Users'          },
  { key: 'ai',       icon: '🤖', label: 'AI Usage'        },
  { key: 'config',   icon: '🚗', label: 'Vehicle Config'  },
  { key: 'versions', icon: '🏷',  label: 'Versions'        },
];

export default function AdminPage() {
  const { user, signOut }     = useAuth();
  const navigate              = useNavigate();
  const [activeTab, setTab]   = useState('users');

  function handleSignOut() {
    signOut();
    navigate('/');
  }

  return (
    <div className="adm-root">

      {/* ── Header ────────────────────────────────────────────────── */}
      <header className="adm-header">
        <div className="adm-header-brand">
          <span className="adm-header-gear">⚙</span>
          <span className="adm-header-name">AutoRestoration</span>
          <span className="adm-admin-badge">ADMIN</span>
        </div>
        <div className="adm-header-user">
          <button className="adm-back-btn" onClick={() => navigate('/dashboard')}>
            ← Dashboard
          </button>
          <span className="adm-username">@{user?.username}</span>
          <button className="adm-signout-btn" onClick={handleSignOut}>Sign Out</button>
        </div>
      </header>

      <div className="adm-body">

        {/* ── Side tabs ─────────────────────────────────────────── */}
        <nav className="adm-nav">
          <p className="adm-nav-heading">Admin Tools</p>
          {TABS.map(t => (
            <button
              key={t.key}
              className={`adm-nav-item ${activeTab === t.key ? 'active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              <span className="adm-nav-icon">{t.icon}</span>
              <span className="adm-nav-label">{t.label}</span>
            </button>
          ))}
        </nav>

        {/* ── Main panel ────────────────────────────────────────── */}
        <main className="adm-main">
          {activeTab === 'users'    && <UsersTab  />}
          {activeTab === 'ai'       && <AiUsageTab />}
          {activeTab === 'config'   && <VehicleConfigTab />}
          {activeTab === 'versions' && <VersionsTab />}
        </main>

      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   USERS TAB
══════════════════════════════════════════════════════════════════ */

function UsersTab() {
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [working, setWorking] = useState(null); // userAccountId being acted on

  const load = () => {
    setLoading(true);
    adminListUsers()
      .then(r => setUsers(r.success ? r.data : []))
      .catch(() => setError('Failed to load users.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  async function doAction(action, id) {
    setWorking(id);
    try {
      if (action === 'lock')       await adminLockUser(id);
      if (action === 'unlock')     await adminUnlockUser(id);
      if (action === 'deactivate') await adminDeactivateUser(id);
      load();
    } catch { setError('Action failed.'); }
    finally  { setWorking(null); }
  }

  return (
    <div className="adm-panel">
      <div className="adm-panel-header">
        <h2 className="adm-panel-title">👥 Users</h2>
        <span className="adm-panel-count">{users.length} accounts</span>
      </div>

      {error   && <p className="adm-error">{error}</p>}
      {loading ? (
        <p className="adm-loading">Loading users…</p>
      ) : (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Display Name</th>
                <th>Email</th>
                <th>Roles</th>
                <th>Type</th>
                <th>Status</th>
                <th>Last Activity</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.userAccountId} className={!u.is_active ? 'adm-row-inactive' : ''}>
                  <td className="adm-cell-username">@{u.username}</td>
                  <td>{u.display_name || '—'}</td>
                  <td>{u.email}</td>
                  <td>
                    {(u.roles || '').split(',').filter(Boolean).map(r => (
                      <span key={r} className={`adm-role-badge ${r.trim()}`}>{r.trim()}</span>
                    ))}
                  </td>
                  <td>{u.account_type || '—'}</td>
                  <td>
                    <span className={`adm-status-badge ${u.is_active ? (u.is_locked ? 'locked' : 'active') : 'inactive'}`}>
                      {!u.is_active ? 'Inactive' : u.is_locked ? 'Locked' : 'Active'}
                    </span>
                  </td>
                  <td className="adm-cell-date">
                    {u.last_activity ? new Date(u.last_activity).toLocaleString() : '—'}
                  </td>
                  <td className="adm-cell-actions">
                    {u.is_active && !u.is_locked && (
                      <button
                        className="adm-action-btn warn"
                        disabled={working === u.userAccountId}
                        onClick={() => doAction('lock', u.userAccountId)}
                      >Lock</button>
                    )}
                    {u.is_active && u.is_locked && (
                      <button
                        className="adm-action-btn ok"
                        disabled={working === u.userAccountId}
                        onClick={() => doAction('unlock', u.userAccountId)}
                      >Unlock</button>
                    )}
                    {u.is_active && (
                      <button
                        className="adm-action-btn danger"
                        disabled={working === u.userAccountId}
                        onClick={() => {
                          if (window.confirm(`Deactivate @${u.username}?`)) {
                            doAction('deactivate', u.userAccountId);
                          }
                        }}
                      >Deactivate</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   AI USAGE TAB
══════════════════════════════════════════════════════════════════ */

function AiUsageTab() {
  const [records,  setRecords]  = useState([]);
  const [summary,  setSummary]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [filters,  setFilters]  = useState({ startDate: '', endDate: '', userAccountId: '' });
  const [users,    setUsers]    = useState([]);

  // Load user list for the dropdown — sorted by first name (display_name)
  useEffect(() => {
    adminListUsers()
      .then(r => {
        if (r.success) {
          const sorted = [...r.data].sort((a, b) => {
            const aName = (a.display_name || a.username || '').toLowerCase();
            const bName = (b.display_name || b.username || '').toLowerCase();
            return aName.localeCompare(bName);
          });
          setUsers(sorted);
        }
      })
      .catch(() => {});
  }, []);

  const load = (f = filters) => {
    setLoading(true);
    const params = {};
    if (f.startDate)     params.startDate     = f.startDate;
    if (f.endDate)       params.endDate       = f.endDate;
    if (f.userAccountId) params.userAccountId = f.userAccountId;

    adminGetAiUsage(params)
      .then(r => {
        if (r.success) { setRecords(r.data.records); setSummary(r.data.summary); }
        else setError('Failed to load AI usage.');
      })
      .catch(() => setError('Failed to load AI usage.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  function handleFilter(e) {
    e.preventDefault();
    load(filters);
  }

  return (
    <div className="adm-panel">
      <div className="adm-panel-header">
        <h2 className="adm-panel-title">🤖 AI Usage</h2>
      </div>

      {/* Filter bar */}
      <form className="adm-filter-bar" onSubmit={handleFilter}>
        <div className="adm-filter-group">
          <label className="adm-filter-label">From</label>
          <input type="date" className="adm-filter-input"
            value={filters.startDate}
            onChange={e => setFilters(f => ({ ...f, startDate: e.target.value }))} />
        </div>
        <div className="adm-filter-group">
          <label className="adm-filter-label">To</label>
          <input type="date" className="adm-filter-input"
            value={filters.endDate}
            onChange={e => setFilters(f => ({ ...f, endDate: e.target.value }))} />
        </div>
        <div className="adm-filter-group">
          <label className="adm-filter-label">User</label>
          <select
            className="adm-filter-input adm-filter-select"
            value={filters.userAccountId}
            onChange={e => setFilters(f => ({ ...f, userAccountId: e.target.value }))}
          >
            <option value="">All Users</option>
            {users.map(u => (
              <option key={u.userAccountId} value={u.userAccountId}>
                {u.display_name || u.username}
              </option>
            ))}
          </select>
        </div>
        <button type="submit" className="adm-filter-btn">Filter</button>
        <button type="button" className="adm-filter-btn secondary"
          onClick={() => { const blank = { startDate:'', endDate:'', userAccountId:'' }; setFilters(blank); load(blank); }}>
          Clear
        </button>
      </form>

      {error && <p className="adm-error">{error}</p>}

      {/* Summary cards */}
      {summary && (
        <div className="adm-summary-cards">
          <div className="adm-summary-card">
            <span className="adm-summary-value">{Number(summary.total_calls).toLocaleString()}</span>
            <span className="adm-summary-label">Total Calls</span>
          </div>
          <div className="adm-summary-card">
            <span className="adm-summary-value">{Number(summary.failed_calls).toLocaleString()}</span>
            <span className="adm-summary-label">Failed</span>
          </div>
          <div className="adm-summary-card">
            <span className="adm-summary-value">{(Number(summary.total_input_tokens) + Number(summary.total_output_tokens)).toLocaleString()}</span>
            <span className="adm-summary-label">Total Tokens</span>
          </div>
          <div className="adm-summary-card highlight">
            <span className="adm-summary-value">${Number(summary.total_cost_usd).toFixed(4)}</span>
            <span className="adm-summary-label">Total Cost (USD)</span>
          </div>
          <div className="adm-summary-card">
            <span className="adm-summary-value">${Number(summary.unbilled_cost_usd).toFixed(4)}</span>
            <span className="adm-summary-label">Unbilled Cost</span>
          </div>
        </div>
      )}

      {loading ? (
        <p className="adm-loading">Loading AI usage…</p>
      ) : (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>User</th>
                <th>Action</th>
                <th>Model</th>
                <th>Input Tokens</th>
                <th>Output Tokens</th>
                <th>Duration</th>
                <th>Status</th>
                <th>Est. Cost</th>
                <th>Billed</th>
              </tr>
            </thead>
            <tbody>
              {records.map(r => (
                <tr key={r.processId} className={r.status === 'FAILED' ? 'adm-row-failed' : ''}>
                  <td className="adm-cell-date">
                    {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                  </td>
                  <td>{r.username || <span className="adm-muted">system</span>}</td>
                  <td><span className="adm-tag">{r.action_type}</span></td>
                  <td className="adm-cell-model">{r.model}</td>
                  <td>{r.input_tokens  != null ? parseInt(r.input_tokens,  10).toLocaleString() : '—'}</td>
                  <td>{r.output_tokens != null ? parseInt(r.output_tokens, 10).toLocaleString() : '—'}</td>
                  <td>{r.duration_ms ? `${parseInt(r.duration_ms, 10).toLocaleString()}ms` : '—'}</td>
                  <td>
                    <span className={`adm-status-badge ${(r.status||'').toLowerCase()}`}>
                      {r.status || '—'}
                    </span>
                  </td>
                  <td>{r.cost_usd != null ? `$${parseFloat(r.cost_usd).toFixed(6)}` : '—'}</td>
                  <td>{r.is_billed ? `✓ ${r.billed_at ? new Date(r.billed_at).toLocaleDateString() : ''}` : <span className="adm-muted">No</span>}</td>
                </tr>
              ))}
              {records.length === 0 && (
                <tr><td colSpan={10} className="adm-empty-row">No records found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   VEHICLE CONFIG TAB
══════════════════════════════════════════════════════════════════ */

function VehicleConfigTab() {
  const fileRef               = useRef(null);

  // ── Browse catalog state ──────────────────────────────────────
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseMakes,   setBrowseMakes]   = useState([]);
  const [browseModels,  setBrowseModels]  = useState([]);
  const [browseYears,   setBrowseYears]   = useState([]);
  const [browsePkgs,    setBrowsePkgs]    = useState([]);
  const [browseEngines, setBrowseEngines] = useState([]);

  const [selMake, setSelMake] = useState('');
  const [selModel,setSelModel]= useState('');
  const [selYear, setSelYear] = useState('');
  const [selPkg,  setSelPkg]  = useState('');
  const [selEng,  setSelEng]  = useState('');

  // Load makes on mount
  useEffect(() => {
    setBrowseLoading(true);
    refMakes()
      .then(r => setBrowseMakes(r.success ? r.data : []))
      .catch(() => {})
      .finally(() => setBrowseLoading(false));
  }, []);

  async function pickMake(make) {
    setSelMake(make); setSelModel(''); setSelYear(''); setSelPkg(''); setSelEng('');
    setBrowseModels([]); setBrowseYears([]); setBrowsePkgs([]); setBrowseEngines([]);
    if (!make) return;
    const r = await refModels(make);
    setBrowseModels(r.success ? r.data : []);
  }

  async function pickModel(model) {
    setSelModel(model); setSelYear(''); setSelPkg(''); setSelEng('');
    setBrowseYears([]); setBrowsePkgs([]); setBrowseEngines([]);
    if (!model) return;
    const r = await refYears(selMake, model);
    setBrowseYears(r.success ? r.data : []);
  }

  async function pickYear(year) {
    setSelYear(year); setSelPkg(''); setSelEng('');
    setBrowsePkgs([]); setBrowseEngines([]);
    if (!year) return;
    const r = await refPackages(selMake, selModel, year);
    setBrowsePkgs(r.success ? r.data : []);
  }

  async function pickPkg(pkg) {
    setSelPkg(pkg); setSelEng('');
    setBrowseEngines([]);
    if (!pkg) return;
    const r = await refEngines(selMake, selModel, selYear, pkg);
    setBrowseEngines(r.success ? r.data : []);
  }

  function pickEngine(eng) { setSelEng(eng); }

  // ── AI generation state ───────────────────────────────────────
  const [aiForm,    setAiForm]   = useState({ make: '', model: '', yearFrom: '', yearTo: '' });
  const [aiLoading, setAiLoad]   = useState(false);
  const [aiResult,  setAiResult] = useState('');
  const [aiError,   setAiError]  = useState('');
  const [aiPreview, setAiPreview]= useState([]);

  // ── Manual upload state ───────────────────────────────────────
  const [jsonText,  setJson]     = useState('');
  const [upResult,  setUpResult] = useState('');
  const [upError,   setUpError]  = useState('');
  const [upLoading, setUpLoad]   = useState(false);
  const [fileName,  setFN]       = useState('');

  // ── AI generate ───────────────────────────────────────────────
  async function handleAiGenerate(e) {
    e.preventDefault();
    if (!aiForm.make || !aiForm.model || !aiForm.yearFrom) {
      setAiError('Make, model, and year from are required.');
      return;
    }
    setAiLoad(true);
    setAiError('');
    setAiResult('');
    setAiPreview([]);
    try {
      const res = await adminAiVehicleConfig(
        aiForm.make, aiForm.model,
        parseInt(aiForm.yearFrom),
        aiForm.yearTo ? parseInt(aiForm.yearTo) : parseInt(aiForm.yearFrom)
      );
      if (res.success) {
        setAiResult(`✓ AI generated and loaded ${res.data.configs_processed} configuration(s) for ${aiForm.make} ${aiForm.model}.`);
        setAiPreview(res.data.preview || []);
      } else {
        setAiError(res.message || 'AI generation failed.');
      }
    } catch (err) {
      setAiError(err.response?.data?.message || 'AI generation failed.');
    } finally {
      setAiLoad(false);
    }
  }

  // ── File upload ───────────────────────────────────────────────
  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setFN(file.name);
    setUpError('');
    setUpResult('');
    const reader = new FileReader();
    reader.onload = ev => {
      const text = ev.target.result;
      try {
        const parsed = JSON.parse(text);
        // Handle wrapped format: { metadata: {...}, vehicles: [...] }
        const array = Array.isArray(parsed)
          ? parsed
          : (parsed.vehicles || parsed.configs || parsed.data || null);
        if (!array) {
          setUpError('JSON must be an array or an object with a "vehicles" key containing the array.');
          return;
        }
        setJson(JSON.stringify(array, null, 2));
      } catch {
        try {
          setJson(JSON.stringify(csvToConfig(text), null, 2));
        } catch {
          setUpError('Could not parse file. Use JSON array or CSV with headers: year,make,model,package,engine');
        }
      }
    };
    reader.readAsText(file);
  }

  function csvToConfig(text) {
    const lines   = text.trim().split('\n');
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g,'_'));
    return lines.slice(1).filter(l => l.trim()).map(line => {
      const vals = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
      const obj  = {};
      headers.forEach((h, i) => { obj[h] = vals[i] || null; });
      if (obj.year) obj.year = parseInt(obj.year);
      return obj;
    });
  }

  async function handleUpload() {
    if (!jsonText.trim()) { setUpError('No data to upload.'); return; }
    setUpLoad(true);
    setUpError('');
    setUpResult('');
    try {
      const parsed = JSON.parse(jsonText);
      const res    = await adminLoadVehicleConfig(parsed);
      if (res.success) {
        const { configs_processed, configs_skipped } = res.data;
        const msg = configs_skipped > 0
          ? `✓ Loaded ${configs_processed} configurations. ${configs_skipped} skipped (missing required fields).`
          : `✓ Successfully loaded ${configs_processed} vehicle configuration(s).`;
        setUpResult(msg);
        setJson('');
        setFN('');
      } else {
        setUpError(res.message || 'Upload failed.');
      }
    } catch (err) {
      setUpError(err.response?.data?.message || err.message || 'Upload failed — check server logs.');
    } finally {
      setUpLoad(false);
    }
  }

  const EXAMPLE = JSON.stringify([
    { year: 1969, make: "Chevrolet", model: "Camaro", package: "Z/28", engine: "302 V8 DZ" },
    { year: 1970, make: "Chevrolet", model: "Chevelle", package: "SS 454", engine: "454 V8 LS6" }
  ], null, 2);

  return (
    <div className="adm-panel">
      <div className="adm-panel-header">
        <h2 className="adm-panel-title">🚗 Vehicle Config</h2>
      </div>

      <p className="adm-panel-desc">
        Add vehicle year/make/model/package/engine combinations to the reference catalog.
        Browse what's already loaded, use AI to generate more, or upload a JSON/CSV file.
      </p>

      {/* ── Section 0: Browse Catalog ──────────────────────────── */}
      <div className="adm-config-section">
        <div className="adm-config-section-header">
          <span className="adm-config-section-icon">🔍</span>
          <div>
            <h3 className="adm-config-section-title">Browse Loaded Catalog</h3>
            <p className="adm-config-section-desc">
              Select from left to right to explore what's already in the reference catalog.
            </p>
          </div>
        </div>

        {browseLoading && <p className="adm-loading">Loading catalog…</p>}

        {!browseLoading && (
          <div className="adm-browse-columns">

            {/* Makes */}
            <div className="adm-browse-col">
              <div className="adm-browse-col-header">
                Make
                <span className="adm-browse-col-count">{browseMakes.length}</span>
              </div>
              <div className="adm-browse-list">
                {browseMakes.length === 0 && (
                  <div className="adm-browse-empty">No makes loaded</div>
                )}
                {browseMakes.map(m => (
                  <button
                    key={m.make}
                    className={`adm-browse-item ${selMake === m.make ? 'selected' : ''}`}
                    onClick={() => pickMake(m.make)}
                  >{m.make}</button>
                ))}
              </div>
            </div>

            {/* Models */}
            <div className="adm-browse-col">
              <div className="adm-browse-col-header">
                Model
                <span className="adm-browse-col-count">{browseModels.length}</span>
              </div>
              <div className="adm-browse-list">
                {!selMake && <div className="adm-browse-hint">← Select a make</div>}
                {selMake && browseModels.length === 0 && <div className="adm-browse-empty">None</div>}
                {browseModels.map(m => (
                  <button
                    key={m.model}
                    className={`adm-browse-item ${selModel === m.model ? 'selected' : ''}`}
                    onClick={() => pickModel(m.model)}
                  >{m.model}</button>
                ))}
              </div>
            </div>

            {/* Years */}
            <div className="adm-browse-col">
              <div className="adm-browse-col-header">
                Year
                <span className="adm-browse-col-count">{browseYears.length}</span>
              </div>
              <div className="adm-browse-list">
                {!selModel && <div className="adm-browse-hint">← Select a model</div>}
                {selModel && browseYears.length === 0 && <div className="adm-browse-empty">None</div>}
                {browseYears.map(y => (
                  <button
                    key={y.year}
                    className={`adm-browse-item ${selYear === String(y.year) ? 'selected' : ''}`}
                    onClick={() => pickYear(String(y.year))}
                  >{y.year}</button>
                ))}
              </div>
            </div>

            {/* Packages */}
            <div className="adm-browse-col">
              <div className="adm-browse-col-header">
                Package / Trim
                <span className="adm-browse-col-count">{browsePkgs.length}</span>
              </div>
              <div className="adm-browse-list">
                {!selYear && <div className="adm-browse-hint">← Select a year</div>}
                {selYear && browsePkgs.length === 0 && <div className="adm-browse-empty">None</div>}
                {browsePkgs.map(p => (
                  <button
                    key={p.package}
                    className={`adm-browse-item ${selPkg === p.package ? 'selected' : ''}`}
                    onClick={() => pickPkg(p.package)}
                  >{p.package}</button>
                ))}
              </div>
            </div>

            {/* Engines */}
            <div className="adm-browse-col">
              <div className="adm-browse-col-header">
                Engine
                <span className="adm-browse-col-count">{browseEngines.length}</span>
              </div>
              <div className="adm-browse-list">
                {!selPkg && <div className="adm-browse-hint">← Select a package</div>}
                {selPkg && browseEngines.length === 0 && <div className="adm-browse-empty">None</div>}
                {browseEngines.map(e => (
                  <button
                    key={e.engine}
                    className={`adm-browse-item ${selEng === e.engine ? 'selected' : ''}`}
                    onClick={() => pickEngine(e.engine)}
                  >{e.engine}</button>
                ))}
              </div>
            </div>

          </div>
        )}

        {/* Selection summary */}
        {selMake && (
          <div className="adm-browse-summary">
            <span className="adm-browse-summary-label">Selected:</span>
            {[selMake, selModel, selYear, selPkg, selEng].filter(Boolean).join(' › ')}
            <button
              className="adm-browse-clear"
              onClick={() => {
                setSelMake(''); setSelModel(''); setSelYear(''); setSelPkg(''); setSelEng('');
                setBrowseModels([]); setBrowseYears([]); setBrowsePkgs([]); setBrowseEngines([]);
              }}
            >✕ Clear</button>
          </div>
        )}
      </div>

      {/* ── Divider ────────────────────────────────────────────── */}
      <div className="adm-config-divider"><span>add to catalog</span></div>

      {/* ── Section 1: AI Generate ─────────────────────────────── */}
      <div className="adm-config-section">
        <div className="adm-config-section-header">
          <span className="adm-config-section-icon">🤖</span>
          <div>
            <h3 className="adm-config-section-title">Ask AI to Generate</h3>
            <p className="adm-config-section-desc">
              Claude will look up all known factory trim packages and engine options
              for the vehicle you specify and load them directly into the catalog.
            </p>
          </div>
        </div>

        <form className="adm-ai-gen-form" onSubmit={handleAiGenerate}>
          <div className="adm-filter-group">
            <label className="adm-filter-label">Make</label>
            <input className="adm-filter-input" placeholder="e.g. Chevrolet"
              value={aiForm.make}
              onChange={e => setAiForm(f => ({ ...f, make: e.target.value }))} />
          </div>
          <div className="adm-filter-group">
            <label className="adm-filter-label">Model</label>
            <input className="adm-filter-input" placeholder="e.g. Camaro"
              value={aiForm.model}
              onChange={e => setAiForm(f => ({ ...f, model: e.target.value }))} />
          </div>
          <div className="adm-filter-group">
            <label className="adm-filter-label">Year From</label>
            <input className="adm-filter-input" placeholder="e.g. 1967" type="number"
              value={aiForm.yearFrom}
              onChange={e => setAiForm(f => ({ ...f, yearFrom: e.target.value }))} />
          </div>
          <div className="adm-filter-group">
            <label className="adm-filter-label">Year To</label>
            <input className="adm-filter-input" placeholder="(optional)" type="number"
              value={aiForm.yearTo}
              onChange={e => setAiForm(f => ({ ...f, yearTo: e.target.value }))} />
          </div>
          <button type="submit" className="adm-filter-btn" disabled={aiLoading}>
            {aiLoading ? '⏳ Generating…' : '🤖 Generate'}
          </button>
        </form>

        {aiError  && <p className="adm-error"  style={{ marginTop: '12px' }}>{aiError}</p>}
        {aiResult && <p className="adm-success" style={{ marginTop: '12px' }}>{aiResult}</p>}

        {aiPreview.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            <p className="adm-config-format-label">Preview (first {aiPreview.length} rows):</p>
            <div className="adm-table-wrap">
              <table className="adm-table">
                <thead>
                  <tr><th>Year</th><th>Make</th><th>Model</th><th>Package</th><th>Engine</th></tr>
                </thead>
                <tbody>
                  {aiPreview.map((r, i) => (
                    <tr key={i}>
                      <td>{r.year}</td><td>{r.make}</td><td>{r.model}</td>
                      <td>{r.package}</td><td>{r.engine}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ── Divider ────────────────────────────────────────────── */}
      <div className="adm-config-divider">
        <span>or upload manually</span>
      </div>

      {/* ── Section 2: Manual Upload ───────────────────────────── */}
      <div className="adm-config-section">
        <div className="adm-config-section-header">
          <span className="adm-config-section-icon">📂</span>
          <div>
            <h3 className="adm-config-section-title">Upload File</h3>
            <p className="adm-config-section-desc">
              Upload a JSON array or CSV file. You can also paste or type JSON directly.
            </p>
          </div>
        </div>

        {/* Format hint */}
        <div className="adm-config-format">
          <p className="adm-config-format-label">JSON format:</p>
          <pre className="adm-config-pre">{EXAMPLE}</pre>
          <p className="adm-config-format-label" style={{ marginTop: '10px' }}>
            CSV: first row headers — <code>year,make,model,package,engine</code>
          </p>
        </div>

        <div className="adm-config-upload-row">
          <input ref={fileRef} type="file" accept=".json,.csv,.txt"
            style={{ display: 'none' }} onChange={handleFileChange} />
          <button className="adm-upload-pick-btn" onClick={() => fileRef.current.click()}>
            📂 Choose File
          </button>
          {fileName && <span className="adm-filename">{fileName}</span>}
        </div>

        <div className="adm-config-editor-wrap">
          <label className="adm-config-editor-label">
            JSON Preview / Manual Entry
            {jsonText && (
              <span className="adm-config-count">
                {(() => { try { return ` — ${JSON.parse(jsonText).length} records`; } catch { return ''; } })()}
              </span>
            )}
          </label>
          <textarea className="adm-config-editor"
            placeholder={`Paste JSON array here, or choose a file above.\n\n${EXAMPLE}`}
            value={jsonText}
            onChange={e => { setJson(e.target.value); setUpError(''); setUpResult(''); }}
            rows={10}
          />
        </div>

        {upError  && <p className="adm-error">{upError}</p>}
        {upResult && <p className="adm-success">{upResult}</p>}

        <div className="adm-config-actions">
          <button className="adm-upload-btn" onClick={handleUpload}
            disabled={upLoading || !jsonText.trim()}>
            {upLoading ? 'Uploading…' : '⬆ Upload to Catalog'}
          </button>
          <button className="adm-clear-btn"
            onClick={() => { setJson(''); setFN(''); setUpError(''); setUpResult(''); }}>
            Clear
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   VERSIONS TAB
══════════════════════════════════════════════════════════════════ */
function VersionsTab() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    adminGetVersions()
      .then(res => {
        if (res.success) setData(res.data);
        else setError(res.message || 'Could not load versions.');
      })
      .catch(() => setError('Could not load versions.'))
      .finally(() => setLoading(false));
  }, []);

  const COMPONENTS = [
    {
      key:    'tables',
      label:  'Database Tables',
      icon:   '🗄',
      file:   'schema/TABLES.sql',
      desc:   'All table DDL, seed data, AUTO_INCREMENT resets',
    },
    {
      key:    'procedures',
      label:  'Stored Procedures',
      icon:   '⚙',
      file:   'schema/AUTORESTORE.sql',
      desc:   'All stored procedures and business logic',
    },
    {
      key:    'ui',
      label:  'React UI',
      icon:   '🖥',
      file:   'autorestoration-ui/package.json',
      desc:   'Front-end application build',
    },
  ];

  function fmt(dt) {
    if (!dt) return '—';
    return new Date(dt).toLocaleString();
  }

  return (
    <div className="adm-section">
      <h2 className="adm-section-title">🏷 Deployed Versions</h2>
      <p style={{ color: '#8a9ab8', fontSize: '0.88rem', marginBottom: 24 }}>
        Shows the version of each component currently deployed on this server.
        Compare these numbers to the <code>-- Version :</code> header at the top of
        each SQL script to know if a re-deploy is needed.
      </p>

      {loading && <div className="adm-loading">Loading…</div>}
      {error   && <div className="adm-error">{error}</div>}

      {!loading && !error && data && (
        <div className="adm-version-grid">
          {COMPONENTS.map(c => {
            const v = data[c.key];
            return (
              <div key={c.key} className="adm-version-card">
                <div className="adm-version-card-header">
                  <span className="adm-version-icon">{c.icon}</span>
                  <span className="adm-version-label">{c.label}</span>
                </div>

                <div className="adm-version-number">
                  v{v?.version ?? '—'}
                </div>

                <div className="adm-version-meta">
                  <div className="adm-version-row">
                    <span className="adm-version-key">Script file</span>
                    <code className="adm-version-val">{c.file}</code>
                  </div>
                  {v?.deployed_at && (
                    <div className="adm-version-row">
                      <span className="adm-version-key">Deployed at</span>
                      <span className="adm-version-val">{fmt(v.deployed_at)}</span>
                    </div>
                  )}
                  <div className="adm-version-row">
                    <span className="adm-version-key">Description</span>
                    <span className="adm-version-val">{c.desc}</span>
                  </div>
                  {v?.notes && (
                    <div className="adm-version-row">
                      <span className="adm-version-key">Notes</span>
                      <span className="adm-version-val">{v.notes}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="adm-version-hint">
        <strong>How to check for updates:</strong> Open each SQL file and look at the
        <code> -- Version :</code> line in the header. If that number is higher than
        what's shown above, re-run <code>bash deploy.sh</code> and then re-source the
        changed SQL files on the server.
      </div>
    </div>
  );
}
